// ============================================================
// หน้า SaleInquiries — คำขอนัดดูห้องจากหน้าเว็บประกาศขาย/เช่า
// ------------------------------------------------------------
// ผู้สนใจกรอกฟอร์มบนหน้าเว็บสาธารณะ (CDS-Sale-Landing-Page)
// แล้วคำขอเข้ามาที่นี่ให้เจ้าหน้าที่ติดตามงานต่อ
//
// สถานะ: ใหม่ → ติดต่อแล้ว → นัดแล้ว → ปิดงาน
// ============================================================

import React, { Component } from "react";
import PropertyNavbar from "../../components/PropertyNavbar.js";
import { loading, GET, alert, getCurrentProject, formatSaleDateTime, formatSaleDate } from "../../components/CustomComponent.js";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";
import { stripJuristic } from "../../components/text-sanitize.js";
import InquiryModal, {
    URGENCY,
    URGENCY_STYLE,
    appointmentUrgency,
    priorityBadge,
} from "../../components/InquiryModal.js";
import {
    INQUIRY_STATUS,
    INQUIRY_STATUS_LIST,
    INQUIRY_STATUS_STYLE,
    INQUIRY_STATUS_TONE,
    localDateKey,
} from "../../constants/sale.js";

import "../../assets/css/bootstrap.css";
import "../../assets/css/style.css";
import "../../assets/css/property.css";
import "../../assets/css/property-theme-cds.css";
import "../../assets/css/sale-dashboard.css";

const EMPTY_SUMMARY = { total: 0, ...Object.fromEntries(INQUIRY_STATUS_LIST.map((i) => [i, 0])), today: 0 };

// วันที่ (YYYY-MM-DD) ของ "นัด" ในรายการ — วันนัดที่ผู้ดูแลตกลงแล้ว (appointment_at) มาก่อน
// ยังไม่ได้นัดจริง ค่อยใช้วันที่ผู้สนใจสะดวก (visit_date) ที่กรอกมาจากหน้าเว็บ
const appointmentDateKey = (item) => String(item?.appointment_at || item?.visit_date || "").slice(0, 10);


export default class SaleInquiries extends Component {
    constructor(props) {
        super(props);
        this.state = {
            data: [],
            filter_status: "",
            filter_search: "",
            // ตัวกรองที่ใช้กับผลที่แสดงอยู่จริง (ไม่ใช่ค่าที่พิมพ์ค้างในช่องแต่ยังไม่กดค้นหา) — ใช้เลือกข้อความ empty state
            applied_filter: false,

            summary: { ...EMPTY_SUMMARY },

            // ★ หน้านี้แสดงเป็น "ห้อง" ไม่ใช่รายคำขอ — คนทำงานคิดเป็นห้อง ไม่ใช่เป็นแถว
            //   กดห้องแล้วเปิดโมดัลจัดการชุดเดียวกับหน้าจัดการทรัพย์ (components/InquiryModal.js)
            modal_inquiry: false,
            active_room: null,
        };
    }

    componentDidMount() {
        if (!localStorage.getItem("cds-acc-tk")) {
            localStorage.clear();
            window.location.href = "/";
            return;
        }
        loading(false);
        this.getData();
    }

    // ตัวเลขสรุปจากคำขอ "ทั้งโครงการ"
    // ★ ไม่สนตัวกรองปัจจุบัน — กดกรองแล้วตัวเลขภาพรวมต้องไม่เพี้ยน
    // ★ นัดวันนี้ เทียบวันที่ตามเวลาเครื่อง (localDateKey) — toISOString() เป็น UTC
    //   ช่วง 00:00–06:59 ของไทยจะนับเป็นเมื่อวาน
    buildSummary = (rows) => {
        const today = localDateKey();
        const summary = { ...EMPTY_SUMMARY };
        rows.forEach((item) => {
            summary.total += 1;
            if (summary[item.status] !== undefined) summary[item.status] += 1;
            // นัดวันนี้ = ต้องเห็นก่อนใคร เจ้าหน้าที่จะได้เตรียมพาชมห้องทัน
            if (appointmentDateKey(item) === today) summary.today += 1;
        });
        return summary;
    };

    getData = async () => {
        const { filter_status, filter_search } = this.state;
        loading(true);
        // ★ page: 0 = เอาทั้งหมดมาจัดกลุ่มตามห้องฝั่งหน้าจอ
        //   ถ้าใช้ page/page_size ปกติ ห้องเดียวกันจะถูกตัดคนละหน้า กลุ่มจะไม่ครบ
        // ★ ดึงทั้งโครงการครั้งเดียว ใช้ทั้งทำสรุปและเป็นรายการ (กรองสถานะฝั่งหน้าจอ)
        //   ยิงรอบที่สองเฉพาะตอนมีคำค้นหา — การค้นหา (ชื่อ/เบอร์/เลขห้อง) ให้ backend เป็นคนตัดสินเหมือนเดิม
        //   เดิมยิง page=0 สองรอบทุกครั้งที่โหลด/บันทึก
        const all = await GET("api/admin/sale-inquiries", { page: 0 });
        const rows = all?.status ? all.data || [] : [];

        let list = rows;
        if (filter_search) {
            const searched = await GET("api/admin/sale-inquiries", { page: 0, data_search: filter_search });
            list = searched?.status ? searched.data || [] : [];
        }
        if (filter_status) list = list.filter((item) => (item.status || INQUIRY_STATUS.NEW) === filter_status);

        this.setState({
            data: list,
            summary: all?.status ? this.buildSummary(rows) : { ...EMPTY_SUMMARY },
            applied_filter: !!(filter_status || filter_search),
        });
        loading(false);
    };

    // จัดกลุ่มคำขอตามห้อง + สรุปสิ่งที่ต้องตัดสินใจของห้องนั้น
    // ★ เรียงห้องที่ยังมีงานค้างขึ้นก่อน (ใหม่มากสุด > ลำดับที่จัดไว้ > วันติดตามใกล้สุด)
    rooms = () => {
        const groups = new Map();
        const now = Date.now();

        this.state.data.forEach((item) => {
            const key = item.address_number || "ไม่ระบุห้อง";
            if (!groups.has(key)) {
                groups.set(key, {
                    room: key,
                    items: [],
                    newCount: 0,
                    priority: null,
                    nextFollow: "",
                    latest: "",
                    urgency: URGENCY.NORMAL,   // 0 = เกินวันนัด, 1 = ใกล้ถึง, 2 = ปกติ
                    appointment: "",           // วันนัดที่ทำให้ห้องนี้ถูกยกขึ้นมา
                });
            }
            const group = groups.get(key);
            group.items.push(item);
            if ((item.status || INQUIRY_STATUS.NEW) === INQUIRY_STATUS.NEW) group.newCount += 1;
            if (item.priority && (group.priority === null || item.priority < group.priority)) group.priority = item.priority;
            if (item.follow_up_at && (!group.nextFollow || item.follow_up_at < group.nextFollow)) group.nextFollow = item.follow_up_at;
            if (item.created_at && item.created_at > group.latest) group.latest = item.created_at;

            // ★ นับเฉพาะงานที่ยังไม่ปิด — งานที่ปิดแล้วเลยวันนัดไปก็ไม่ใช่เรื่องต้องตามอีก
            const urgency = appointmentUrgency(item, now);
            if (urgency < group.urgency) {
                group.urgency = urgency;
                group.appointment = item.appointment_at;
            }
        });

        return [...groups.values()].sort((a, b) => {
            // เรียงตามความเร่งด่วนของวันนัดก่อนเสมอ : เกิน > ใกล้ถึง > ปกติ
            if (a.urgency !== b.urgency) return a.urgency - b.urgency;
            if (b.newCount !== a.newCount) return b.newCount - a.newCount;
            const pa = a.priority ?? 9999;
            const pb = b.priority ?? 9999;
            if (pa !== pb) return pa - pb;
            if (a.nextFollow !== b.nextFollow) return (a.nextFollow || "9999").localeCompare(b.nextFollow || "9999");
            return (b.latest || "").localeCompare(a.latest || "");
        });
    };

    openRoom = (group) => this.setState({ modal_inquiry: true, active_room: group });

    closeRoom = () => this.setState({ modal_inquiry: false, active_room: null });

    // หัวข้อกลุ่ม — ชุดเดียวกับแดชบอร์ดงานขาย (dash-section)
    section = (title, note) => (
        <div className="dash-section">
            <span className="dash-section__dot" />
            <span className="fw-bold text-dark text-nowrap">{title}</span>
            {!!note && <span className="text-12 text-secondary text-nowrap">{note}</span>}
            <div className="dash-section__rule" />
        </div>
    );

    // ตัวเลขสรุป 1 ใบ — หน้าตาเดียวกับ tile ของแดชบอร์ด
    tile = (label, value, tone) => (
        <div className="prop-card prop-in h-100">
            <div className="p-4">
                <span className="dash-label">{label}</span>
                <div className="d-flex align-items-baseline gap-2 mt-2">
                    <span className="dash-value" style={tone ? { color: tone } : undefined}>{value}</span>
                    <span className="text-12 text-secondary">รายการ</span>
                </div>
            </div>
        </div>
    );

    // ส่งออก Excel — 1 แถวต่อ 1 คำขอ ตามตัวกรองที่เห็นบนหน้าจอ
    // ★ state.data ดึงด้วย page: 0 (ทั้งหมดหลังกรอง) อยู่แล้ว จึงครบทุกรายการ · เรียงตามห้องเหมือนที่เห็นบนหน้าจอ
    exportExcel = async () => {
        const rows = this.rooms().flatMap((group) => group.items.map((item) => ({ room: group.room, item })));
        if (!rows.length) {
            alert("", "warning", "แจ้งเตือน", "ไม่พบข้อมูลสำหรับ Export");
            return;
        }
        loading(true);
        try {
            const wb = new ExcelJS.Workbook();
            const ws = wb.addWorksheet("นัดหมาย");
            ws.columns = [
                { header: "เลขห้อง", key: "room", width: 16 },
                { header: "ชื่อ", key: "full_name", width: 24 },
                { header: "เบอร์โทร", key: "phone", width: 16 },
                { header: "อีเมล", key: "email", width: 28 },
                { header: "ซื้อ/เช่า", key: "interest_type", width: 12 },
                { header: "วันเวลานัดชม", key: "appointment", width: 20 },
                { header: "วันติดตาม", key: "follow_up_at", width: 20 },
                { header: "สถานะ", key: "status", width: 12 },
                { header: "ข้อความจากลูกค้า", key: "message", width: 40 },
                { header: "หมายเหตุเจ้าหน้าที่", key: "staff_note", width: 40 },
                { header: "ผู้ดูแล", key: "handled_by", width: 20 },
                { header: "วันที่ส่งคำขอ", key: "created_at", width: 20 },
            ];
            ws.getRow(1).font = { bold: true };
            ws.getColumn("phone").numFmt = "@";   // เบอร์ขึ้นต้นด้วย 0 ต้องเป็นข้อความ
            rows.forEach(({ room, item }) => {
                // นัดชม = วันนัดที่ยืนยันแล้ว ถ้ายังไม่มี ใช้วันเวลาที่ผู้สนใจสะดวก (เหมือนที่ใช้นับ "นัดวันนี้")
                const appointment = item.appointment_at
                    ? formatSaleDateTime(item.appointment_at)
                    : [formatSaleDate(item.visit_date), item.visit_time].filter(Boolean).join(" ");
                ws.addRow({
                    room,
                    full_name: item.full_name || "",
                    phone: item.phone || "",
                    email: item.email || "",
                    interest_type: item.interest_type || "",
                    appointment,
                    follow_up_at: formatSaleDateTime(item.follow_up_at),
                    status: item.status || INQUIRY_STATUS.NEW,
                    message: item.message || "",
                    staff_note: item.staff_note || "",
                    handled_by: item.handled_by || "",
                    created_at: formatSaleDateTime(item.created_at),
                });
            });
            const buffer = await wb.xlsx.writeBuffer();
            saveAs(new Blob([buffer]), "นัดหมาย.xlsx");
        } catch (e) {
            console.error("exportExcel error:", e);
            alert("", "warning", "แจ้งเตือน", "Export ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
        } finally {
            loading(false);
        }
    };

    clearFilter = () => this.setState({ filter_status: "", filter_search: "" }, this.getData);

    render() {
        const s = this.state;
        const groups = this.rooms();
        const project = getCurrentProject();

        return (
            <div className="w-100 min-vh-100 prop-page">
                <PropertyNavbar menuKey="sales-appointment" page1="ลูกค้า" page2="นัดหมาย" projectName={project?.project_name} />

                <div className="body p-4">
                    {/* หัวหน้า — โครงเดียวกับแดชบอร์ด/จัดการทรัพย์ */}
                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-2 gap-3 prop-in">
                        <div>
                            <h3 className="fw-bold mb-1 text-dark">นัดหมาย</h3>
                            <div className="text-secondary text-14">
                                {stripJuristic(project?.project_name) || "-"} · คำขอนัดดูห้องจากหน้าเว็บประกาศขาย/เช่า
                            </div>
                        </div>
                    </div>

                    {/* ---------------- ตัวเลขสรุป ---------------- */}
                    {this.section("ภาพรวมคำขอ", "ณ ตอนนี้")}
                    <div className="row g-3">
                        <div className="col-6 col-md-4 col-xl-2">{this.tile("คำขอทั้งหมด", s.summary.total)}</div>
                        <div className="col-6 col-md-4 col-xl-2">
                            {this.tile("ยังไม่ได้ติดต่อ", s.summary[INQUIRY_STATUS.NEW], INQUIRY_STATUS_TONE[INQUIRY_STATUS.NEW])}
                        </div>
                        <div className="col-6 col-md-4 col-xl-2">
                            {this.tile(INQUIRY_STATUS.CONTACTED, s.summary[INQUIRY_STATUS.CONTACTED], INQUIRY_STATUS_TONE[INQUIRY_STATUS.CONTACTED])}
                        </div>
                        <div className="col-6 col-md-4 col-xl-2">
                            {this.tile(INQUIRY_STATUS.APPOINTED, s.summary[INQUIRY_STATUS.APPOINTED], INQUIRY_STATUS_TONE[INQUIRY_STATUS.APPOINTED])}
                        </div>
                        <div className="col-6 col-md-4 col-xl-2">{this.tile("นัดวันนี้", s.summary.today, "#146c43")}</div>
                        <div className="col-6 col-md-4 col-xl-2">
                            {this.tile(INQUIRY_STATUS.CLOSED, s.summary[INQUIRY_STATUS.CLOSED], INQUIRY_STATUS_TONE[INQUIRY_STATUS.CLOSED])}
                        </div>
                    </div>

                    {/* ---------------- ตัวกรอง ---------------- */}
                    {this.section("คำขอนัดดูห้อง", `${groups.length} ห้อง`)}
                    <div className="prop-card prop-in mb-3">
                        <div className="p-3 d-flex flex-wrap gap-2 align-items-center">
                            <select
                                className="prop-field prop-select"
                                style={{ width: 180 }}
                                value={s.filter_status}
                                onChange={(e) => this.setState({ filter_status: e.target.value }, this.getData)}
                            >
                                <option value="">ทุกสถานะ</option>
                                {INQUIRY_STATUS_LIST.map((i) => (
                                    <option key={i} value={i}>{i}</option>
                                ))}
                            </select>
                            <input
                                className="prop-field"
                                style={{ width: 260 }}
                                placeholder="ค้นหา ชื่อ / เบอร์ / เลขห้อง"
                                value={s.filter_search}
                                onChange={(e) => this.setState({ filter_search: e.target.value })}
                                onKeyUp={(e) => e.key === "Enter" && this.getData()}
                            />
                            <button className="prop-btn prop-btn--sm" onClick={() => this.getData()}>
                                <span className="icon text-12">{"\uf002"}</span> ค้นหา
                            </button>
                            <button className="prop-btn prop-btn--ghost prop-btn--sm" data-testid="export-excel" onClick={this.exportExcel}>
                                <span className="icon text-12">{"\uf019"}</span> ส่งออก Excel
                            </button>
                        </div>
                    </div>

                    {/* ---------------- รายการแบบ "ห้อง" ----------------
                        การ์ดละห้อง : สีบอกความเร่งด่วนของวันนัด แล้วกดเข้าโมดัลจัดการ */}
                    <div className="row g-3">
                        {!groups.length ? (
                            <div className="col-12">
                                {/* ★ แยกกรณี "กรองแล้วไม่เจอ" ออกจาก "ยังไม่มีคำขอเลย"
                                    เดิมขึ้น "ยังไม่มีคำขอนัดดูห้อง" แม้มีคำขออยู่แต่ไม่ตรงตัวกรอง */}
                                <div className="prop-card prop-in">
                                    {s.applied_filter ? (
                                        <div className="prop-empty">
                                            <div className="prop-empty__icon icon">{"\uf002"}</div>
                                            <div className="fw-bold text-dark mb-1">ไม่พบคำขอตามเงื่อนไข</div>
                                            <div className="text-12">ลองเปลี่ยนสถานะหรือคำค้นหา</div>
                                            <button type="button" className="prop-btn prop-btn--ghost prop-btn--sm mt-3" onClick={this.clearFilter}>
                                                ล้างตัวกรอง
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="prop-empty">
                                            <div className="prop-empty__icon icon">{"\uf073"}</div>
                                            <div className="fw-bold text-dark mb-1">ยังไม่มีคำขอนัดดูห้อง</div>
                                            <div className="text-12">คำขอจากหน้าเว็บประกาศขาย/เช่าจะมาโผล่ที่นี่</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            groups.map((group) => (
                                <div className="col-12 col-md-6 col-xl-4" key={group.room}>
                                    <div
                                        className="prop-card prop-in h-100 pointer"
                                        data-testid={`room-card-${group.room}`}
                                        // ★ สีการ์ดบอกความเร่งด่วนของวันนัดก่อน (เกิน = แดง, ใกล้ถึง = เหลือง)
                                        //   ไม่มีวันนัดค้าง ค่อยใช้สีเตือน "ยังไม่ได้ติดต่อ"
                                        style={
                                            URGENCY_STYLE[group.urgency]?.card ||
                                            (group.newCount ? { borderColor: "#dc3545", background: "#fff5f5" } : undefined)
                                        }
                                        onClick={() => this.openRoom(group)}
                                    >
                                        <div className="p-3">
                                            <div className="d-flex justify-content-between align-items-start gap-2">
                                                <div>
                                                    <div className="fw-bold text-16">ห้อง {group.room}</div>
                                                    <small className="text-secondary">คำขอทั้งหมด {group.items.length} รายการ</small>
                                                </div>
                                                {/* ★ ป้ายเตือนวันนัดอยู่มุมขวาบน — กวาดตาเห็นก่อนอ่านรายละเอียดการ์ด */}
                                                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                                    {!!URGENCY_STYLE[group.urgency] && (
                                                        <span
                                                            className="px-3 py-1 rounded-pill text-12 fw-bold text-nowrap"
                                                            style={URGENCY_STYLE[group.urgency].badge}
                                                            title={group.appointment ? formatSaleDateTime(group.appointment) : ""}
                                                        >
                                                            {URGENCY_STYLE[group.urgency].label}
                                                        </span>
                                                    )}
                                                    {/* ลำดับที่ผู้ดูแลจัดไว้ของห้องนี้ — โชว์เป็นคำ (ด่วนมาก/ปานกลาง/ไม่ด่วน) */}
                                                    {!!priorityBadge(group.priority) && (
                                                        <span
                                                            className="px-2 py-1 rounded-pill text-12 fw-bold text-nowrap"
                                                            style={priorityBadge(group.priority).style}
                                                            title="ลำดับการติดต่อ"
                                                        >
                                                            {priorityBadge(group.priority).label}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* วันนัดที่ทำให้ห้องนี้ถูกยกขึ้นมา — บอกใต้ชื่อห้องให้ครบ ไม่ต้องชี้ที่ป้าย */}
                                            {!!URGENCY_STYLE[group.urgency] && !!group.appointment && (
                                                <div className="text-12 mt-2" style={{ color: URGENCY_STYLE[group.urgency].card.borderColor }}>
                                                    นัดชม {formatSaleDateTime(group.appointment)}
                                                </div>
                                            )}

                                            <div className="d-flex flex-wrap gap-2 mt-3">
                                                {INQUIRY_STATUS_LIST.map((status) => {
                                                    const count = group.items.filter(
                                                        (i) => (i.status || INQUIRY_STATUS.NEW) === status
                                                    ).length;
                                                    if (!count) return null;
                                                    return (
                                                        <span
                                                            key={status}
                                                            className="px-3 py-1 rounded-pill text-12"
                                                            style={INQUIRY_STATUS_STYLE[status]}
                                                        >
                                                            {status} {count}
                                                        </span>
                                                    );
                                                })}
                                            </div>

                                            <div className="text-secondary text-12 mt-3">
                                                {group.nextFollow
                                                    ? `ติดตามครั้งถัดไป ${formatSaleDateTime(group.nextFollow)}`
                                                    : "ยังไม่ได้ตั้งวันติดตาม"}
                                            </div>
                                            <div className="text-secondary text-12">
                                                คำขอล่าสุด {formatSaleDateTime(group.latest) || "-"}
                                            </div>

                                            <button className="prop-btn prop-btn--ghost prop-btn--sm w-100 mt-3">จัดการนัดหมาย</button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* ★ โมดัลจัดการชุดเดียวกับหน้าจัดการทรัพย์ — แก้ที่เดียวใช้ทั้งสองหน้า */}
                <InquiryModal
                    show={s.modal_inquiry}
                    roomLabel={s.active_room?.room}
                    inquiries={s.active_room?.items || []}
                    onClose={this.closeRoom}
                    onSaved={this.getData}
                />
            </div>
        );
    }
}
